/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useActions } from '../../providers/actions-provider/actions.context';

import { useStore } from '../../../store';
import { IDevice } from '../../../store/modules/devices/i-devices';
import SecondaryButton from '../../lib/secondary-button';
import { EDIT_DEVICE_PATH } from '../../routing/paths.constants';
import './devices.less';
import checkPermission from '../../../utils/permissions/permissions';

// Remove details object
const useDevices = () => {
  const { devicesActions, userActions } = useActions();
  const { devices } = useStore();
  const [filteredDevices, setFilteredDevices] = useState<IDevice[]>([]);
  const [keyModalVisible, setApiKeyModalVisible] = useState(false);
  const [otsModalVisible, setOtsModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deviceLoaded, setDeviceLoaded] = useState<IDevice>();
  const [otsqr, setOtsqr] = useState<Blob>();
  const [apiKey, setApiKey] = useState('');
  const [otsNumber, setOtsNumber] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { push } = useHistory();
  const hasNotWritePermission = !checkPermission('devices', 'write');
  const columns = useMemo(() => [
    {
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      sorter: (a: IDevice, b: IDevice) => a.name.localeCompare(b.name),
      // eslint-disable-next-line react/display-name
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      sorter: (a: IDevice, b: IDevice) => a.description.localeCompare(b.description),
      // eslint-disable-next-line react/display-name
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: 'Group code',
      key: 'group_code',
      dataIndex: 'group_code',
      sorter: (a: IDevice, b: IDevice) => a.group_code.localeCompare(b.group_code),
      filterIcon: () => <SearchOutlined />,

    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      sorter: (a: IDevice, b: IDevice) => a.status.localeCompare(b.status),
      filterIcon: () => <SearchOutlined />,
      render: (text:string) => <div className={`devices__status devices__status--${text === 'active' ? 'active' : 'inactive'}`}><span>{text}</span></div>,

    },
    {
      title: 'Created time',
      key: 'createdTime',
      dataIndex: 'createdTime',
      filterIcon: () => <SearchOutlined />,
      sorter: (a: IDevice, b: IDevice) => a.createdTime - b.createdTime,
      render: (text: string) => DateTime.fromMillis(+text).toLocaleString({
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }),
    },
    {
      title: '',
      key: 'apikey',
      dataIndex: 'apikey',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (_:string, record:IDevice) => (
        <SecondaryButton
          type="button"
          className="button-edit ant-btn--small"
          onClick={async () => {
            setIsLoading(true);
            setApiKeyModalVisible(true);
            const apiData = await userActions.getApiKey(record.id);
            setApiKey(apiData.apikey);
            setIsLoading(false);
          }}
        >
          API KEY
        </SecondaryButton>
      ),
    },
    {
      title: '',
      key: 'details',
      dataIndex: 'details',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (_:string, record:IDevice) => (
        <SecondaryButton
          type="button"
          className="button-edit ant-btn--small"
          onClick={async () => {
            setIsLoading(true);
            setOtsModalVisible(true);
            const otsNumberData = await userActions.getDeviceOtsNumber(record.id);
            const otsData = await userActions.getDeviceOts(otsNumberData);
            setOtsqr(otsData);
            setOtsNumber(otsNumberData);
            setIsLoading(false);
          }}
        >
          OTS
        </SecondaryButton>
      ),
    },
    {
      title: '',
      key: 'details',
      dataIndex: 'details',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (_:string, record:IDevice) => (
        <SecondaryButton
          type="button"
          className="button-edit ant-btn--small"
          onClick={() => {
            push(`${EDIT_DEVICE_PATH}/${record.id}`);
          }}
        >
          {hasNotWritePermission ? 'View' : 'Edit'}
        </SecondaryButton>
      ),
    },
    {
      title: '',
      key: 'delete',
      dataIndex: 'delete',
      width: 50,
      // eslint-disable-next-line react/display-name
      render: (_:string, record:IDevice) => (
        <SecondaryButton
          disabled={hasNotWritePermission}
          type="button"
          className="button-edit ant-btn--small"
          onClick={() => {
            setDeleteModalVisible(true);
            setDeviceLoaded(record);
          }}
        >
          Delete
        </SecondaryButton>
      ),
    },
  ], []);

  const filters = useMemo(() => {
    const filteredFilters = columns.filter((el) => el.key !== 'createdTime')
      .map((column) => ({ name: column.title, value: column.key }));
    filteredFilters.pop();
    return filteredFilters;
  },
  []);

  useEffect(() => {
    devicesActions.getDevicesList();
  }, []);

  useEffect(() => {
    if (devices.loaded) {
      setFilteredDevices(devices.data);
    }
  }, [devices]);

  return useMemo(() => ({
    onSearch: (e: string, currentFilter: string) => {
      const filtered = devices.data.filter((device) => {
        const copyAsset = JSON.parse(JSON.stringify(device));
        if (copyAsset[currentFilter].toLowerCase().indexOf(e.toLowerCase()) > -1) {
          return device;
        }
        return null;
      });
      setFilteredDevices([...filtered]);
    },
    onCloseKeyPopup: () => {
      setApiKeyModalVisible(!keyModalVisible);
      setApiKey('');
    },
    onCloseOtsPopup: () => {
      setOtsModalVisible(!otsModalVisible);
      setOtsqr(undefined);
      setOtsNumber(undefined);
    },
    onDeleteDevice: () => {
      if (deviceLoaded && deviceLoaded.id) {
        devicesActions.deleteDevice(deviceLoaded.id, false);
      }
      setDeleteModalVisible(false);
    },
    onCloseDeleteModal: () => {
      setDeleteModalVisible(false);
    },
    deleteModalVisible,
    filteredDevices,
    filters,
    columns,
    keyModalVisible,
    apiKey,
    isLoading,
    otsModalVisible,
    otsqr,
    otsNumber,
  }), [
    devices,
    filteredDevices,
    apiKey,
    isLoading,
    keyModalVisible,
    otsModalVisible,
    otsNumber,
    deleteModalVisible,
  ]);
};
export default useDevices;
